<!--
  The Threads tab's sidebar: the user's Inbox, their own folders, Archive, and
  All. Personal -- where a thread sits here is this user's alone; a colleague on
  the same thread keeps it wherever they put it.

  Presentational: the parent owns the folders and counts and makes the API
  calls, since the bulk "Move to" menu needs the same folder list. This only
  says what was clicked or typed.

  `box` is 'inbox', 'archive', 'all', or a folder id as a string.
-->
<template>
  <nav class="w-48 shrink-0 text-sm select-none">
    <ul class="flex flex-col gap-0.5">
      <li>
        <button type="button" @click="$emit('select', 'inbox')" :class="rowClass('inbox')" class="w-full">
          <InboxIcon class="w-4 h-4 shrink-0" />
          <span class="flex-1 text-left truncate">Inbox</span>
          <span class="text-xs tabular-nums">{{ countText(counts?.inbox) }}</span>
        </button>
      </li>
    </ul>

    <div class="mt-3 mb-1 px-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      <span>Folders</span>
      <button
        type="button"
        @click="startCreate"
        title="New folder"
        class="text-gray-400 hover:text-gray-700"
      ><PlusIcon class="w-4 h-4" /></button>
    </div>
    <ul class="flex flex-col gap-0.5">
      <li v-for="folder in folders" :key="folder.id" class="group relative">
        <form v-if="renamingId === folder.id" @submit.prevent="submitRename(folder)" class="px-1">
          <input
            ref="renameInput"
            v-model="draftName"
            maxlength="64"
            @keydown.esc="cancelEdit"
            @blur="submitRename(folder)"
            class="w-full border rounded-md px-2 py-1 text-sm"
          />
        </form>
        <template v-else>
          <button
            type="button"
            @click="$emit('select', String(folder.id))"
            @dblclick="startRename(folder)"
            :class="rowClass(String(folder.id))"
            class="w-full"
            :title="folder.name"
          >
            <FolderIcon class="w-4 h-4 shrink-0" />
            <span class="flex-1 text-left truncate">{{ folder.name }}</span>
            <span class="text-xs tabular-nums group-hover:invisible">{{ countText(folderCount(folder.id)) }}</span>
          </button>
          <!-- Over the count on hover: rename and delete. -->
          <span class="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1">
            <button type="button" @click.stop="startRename(folder)" title="Rename" class="text-gray-400 hover:text-gray-700">
              <PencilIcon class="w-3.5 h-3.5" />
            </button>
            <button type="button" @click.stop="$emit('remove', folder)" title="Delete folder" class="text-gray-400 hover:text-red-600">
              <TrashIcon class="w-3.5 h-3.5" />
            </button>
          </span>
        </template>
      </li>
      <li v-if="creating">
        <form @submit.prevent="submitCreate" class="px-1">
          <input
            ref="createInput"
            v-model="draftName"
            maxlength="64"
            placeholder="Folder name"
            @keydown.esc="cancelEdit"
            @blur="submitCreate"
            class="w-full border rounded-md px-2 py-1 text-sm"
          />
        </form>
      </li>
      <li v-if="!folders.length && !creating" class="px-2 py-1 text-xs text-gray-400">
        No folders yet.
      </li>
    </ul>

    <ul class="flex flex-col gap-0.5 mt-3 pt-3 border-t border-gray-200">
      <li>
        <button type="button" @click="$emit('select', 'archive')" :class="rowClass('archive')" class="w-full">
          <ArchiveBoxIcon class="w-4 h-4 shrink-0" />
          <span class="flex-1 text-left truncate">Archive</span>
          <span class="text-xs tabular-nums">{{ countText(counts?.archive) }}</span>
        </button>
      </li>
      <li>
        <button type="button" @click="$emit('select', 'all')" :class="rowClass('all')" class="w-full"
                title="Every conversation, wherever you've put it">
          <InboxStackIcon class="w-4 h-4 shrink-0" />
          <span class="flex-1 text-left truncate">All</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script setup>
import { ref, nextTick, defineProps, defineEmits } from 'vue'
import { InboxIcon, InboxStackIcon, ArchiveBoxIcon, FolderIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/vue/20/solid'

const props = defineProps({
  box: { type: String, required: true },
  folders: { type: Array, default: () => [] },
  // {inbox, archive, folders: {id: n}}; null until loaded.
  counts: { type: Object, default: null },
})
const emit = defineEmits(['select', 'create', 'rename', 'remove'])

const creating = ref(false)
const renamingId = ref(null)
const draftName = ref('')
const createInput = ref(null)
const renameInput = ref(null)

function rowClass(key) {
  const base = 'flex items-center gap-2 px-2 py-1.5 rounded-md'
  return props.box === key
    ? `${base} bg-indigo-50 text-indigo-700 font-medium`
    : `${base} text-gray-700 hover:bg-gray-100`
}

function folderCount(id) {
  return props.counts?.folders?.[id]
}

// Blank rather than 0, so an empty box reads as empty rather than as a number.
function countText(n) {
  return n ? n : ''
}

function startCreate() {
  renamingId.value = null
  draftName.value = ''
  creating.value = true
  nextTick(() => createInput.value?.focus())
}

function startRename(folder) {
  creating.value = false
  draftName.value = folder.name
  renamingId.value = folder.id
  // Inside a v-for, a template ref is an array.
  nextTick(() => [].concat(renameInput.value || [])[0]?.focus())
}

function cancelEdit() {
  creating.value = false
  renamingId.value = null
  draftName.value = ''
}

// Enter and blur both land here; the first one through clears the state, so
// the second is a no-op rather than a duplicate request.
function submitCreate() {
  if (!creating.value) return
  const name = draftName.value.trim()
  cancelEdit()
  if (name) emit('create', name)
}

function submitRename(folder) {
  if (renamingId.value !== folder.id) return
  const name = draftName.value.trim()
  cancelEdit()
  if (name && name !== folder.name) emit('rename', { folder, name })
}
</script>
